import React, { useState, useEffect, useCallback } from 'react';
import webhookService from '../services/webhookService';
import { format } from 'date-fns';
import { FiPlusCircle, FiTrash2, FiAlertCircle } from 'react-icons/fi';

const WebhooksPage = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ event: '', targetUrl: '' });
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [hooks, eventList] = await Promise.all([
        webhookService.getWebhooks(),
        webhookService.getWebhookEvents()
      ]);
      setWebhooks(hooks);
      setEvents(eventList);
      if (eventList.length > 0) {
        setNewWebhook(prev => ({ ...prev, event: eventList[0] }));
      }
    } catch (err) {
      setError('Failed to load data.');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    try {
      await webhookService.createWebhook(newWebhook);
      fetchData();
      setIsModalOpen(false);
      setModalError('');
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to create webhook.');
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      try {
        await webhookService.deleteWebhook(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete webhook.');
      }
    }
  };

  const openModal = () => {
    setNewWebhook({ event: events[0] || '', targetUrl: '' });
    setError('');
    setModalError('');
    setIsModalOpen(true);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Webhook Management</h1>
        <button onClick={openModal} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark">
          <FiPlusCircle className="mr-2" /> Create New Webhook
        </button>
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Event</th>
              <th className="px-4 py-3 text-left">Target URL</th>
              <th className="px-4 py-3 text-left">Last Failure</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {webhooks.map(hook => (
              <tr key={hook._id}>
                <td className="px-4 py-4 font-mono text-sm">{hook.event}</td>
                <td className="px-4 py-4 text-sm">{hook.targetUrl}</td>
                <td className="px-4 py-4 text-sm">
                  {hook.lastFailure?.date ? (
                    <span className="text-red-600 flex items-center" title={`[${hook.lastFailure.statusCode}] ${hook.lastFailure.responseBody}`}>
                      <FiAlertCircle className="mr-2" />
                      {format(new Date(hook.lastFailure.date), 'dd-MM-yyyy hh:mm a')}
                    </span>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </td>
                <td className="px-4 py-4 text-right">
                  <button onClick={() => handleDeleteWebhook(hook._id)} className="text-red-600 hover:text-red-900"><FiTrash2 /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Create New Webhook</h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            <form onSubmit={handleCreateWebhook} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Event</label>
                <select name="event" value={newWebhook.event} onChange={(e) => setNewWebhook({...newWebhook, event: e.target.value})} className="mt-1 p-2 border rounded-md w-full">
                  {events.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Target URL</label>
                <input name="targetUrl" type="url" value={newWebhook.targetUrl} onChange={(e) => setNewWebhook({...newWebhook, targetUrl: e.target.value})} placeholder="https://yourapi.com/webhook" required className="mt-1 p-2 border rounded-md w-full" />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setModalError('');
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md"
                >
                  Cancel
                </button>
                <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md">Create Webhook</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksPage;
